// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import * as JsonCodec from "../primitives/JsonCodec.res.js";
import * as Navigation from "../primitives/Navigation.res.js";
import * as QueryString from "../primitives/QueryString.res.js";
import * as Primitive_int from "@rescript/runtime/lib/es6/Primitive_int.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";

function totalPages(totalCount, nodesPerPage) {
  if (nodesPerPage <= 0 || totalCount <= 0) {
    return 1;
  } else if (Primitive_int.mod_(totalCount, nodesPerPage) === 0) {
    return Primitive_int.div(totalCount, nodesPerPage);
  } else {
    return Primitive_int.div(totalCount, nodesPerPage) + 1 | 0;
  }
}

function connectionArguments(currentPage, previousPage, totalCount, cursors) {
  if (currentPage === 1) {
    return {
      first: 10
    };
  }
  let startCursor = cursors[0];
  let exit = 0;
  if (startCursor !== undefined) {
    if (currentPage === (previousPage - 1 | 0)) {
      return {
        last: 10,
        before: startCursor
      };
    }
    exit = 2;
  } else {
    exit = 2;
  }
  if (exit === 2) {
    let endCursor = cursors[1];
    if (endCursor !== undefined && currentPage === (previousPage + 1 | 0)) {
      return {
        first: 10,
        after: endCursor
      };
    }
  }
  if (currentPage === totalPages(totalCount, 10)) {
    return {
      last: 10
    };
  }
}

function initialState(filters) {
  return {
    currentPage: 1,
    previousPage: -1,
    filters: filters,
    connectionArguments: {
      first: 10
    }
  };
}

function reducer(state, action) {
  switch (action.TAG) {
    case "Navigated" :
      let nextPage = action.nextPage;
      let previousPage = state.currentPage;
      let maybeConnectionArguments = connectionArguments(nextPage, previousPage, action.totalCount, action.cursors);
      if (maybeConnectionArguments === undefined) {
        return state;
      }
      let newrecord = {...state};
      newrecord.connectionArguments = maybeConnectionArguments;
      newrecord.previousPage = previousPage;
      newrecord.currentPage = nextPage;
      return newrecord;
    case "Searched" :
      let searchQuery = action._0;
      let tmp = searchQuery === "" ? undefined : searchQuery;
      return {
        currentPage: 1,
        previousPage: -1,
        searchQuery: tmp,
        filters: state.filters,
        connectionArguments: {
          first: 10
        }
      };
    case "FiltersUpdated" :
      return {
        currentPage: 1,
        previousPage: -1,
        searchQuery: state.searchQuery,
        filters: action._0(state.filters),
        connectionArguments: {
          first: 10
        }
      };
    case "Reset" :
      return action._0;
  }
}

function nextPage(state, action, totalPages) {
  let match = state.currentPage;
  if ((match === 0 || match === 1) && (totalPages === 0 || totalPages === 1)) {
    return;
  }
  switch (action) {
    case "First" :
      if (match >= 1) {
        return 1;
      } else {
        return;
      }
    case "Prev" :
      if (match > totalPages) {
        return totalPages;
      } else if (match > 1) {
        return match - 1 | 0;
      } else {
        return;
      }
    case "Next" :
      if (match > totalPages) {
        return totalPages;
      } else if (match >= 1 && match < totalPages) {
        return match + 1 | 0;
      } else {
        return;
      }
    case "Last" :
      if (match >= 1) {
        return totalPages;
      } else {
        return;
      }
  }
}

function encoder(param) {
  let connectionArguments = param.connectionArguments;
  return [
    param.currentPage,
    param.previousPage,
    param.searchQuery,
    Primitive_option.some(param.filters),
    connectionArguments.first,
    connectionArguments.last,
    connectionArguments.after,
    connectionArguments.before
  ];
}

function value(filtersJsonCodec, initialFilters) {
  return JsonCodec.object8(encoder, state => ({
    TAG: "Ok",
    _0: {
      currentPage: Stdlib_Option.getOr(state[0], 1),
      previousPage: Stdlib_Option.getOr(state[1], -1),
      searchQuery: state[2],
      filters: Stdlib_Option.getOr(state[3], initialFilters),
      connectionArguments: {
        first: state[4],
        last: state[5],
        after: state[6],
        before: state[7]
      }
    }
  }), JsonCodec.optional(JsonCodec.field("page", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("previousPage", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("search", JsonCodec.string)), JsonCodec.optional(JsonCodec.field("filters", filtersJsonCodec)), JsonCodec.optional(JsonCodec.field("first", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("last", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("after", JsonCodec.string)), JsonCodec.optional(JsonCodec.field("before", JsonCodec.string)));
}

function use(initialState, filtersJsonCodec) {
  let codec = value(filtersJsonCodec, initialState.filters);
  let navigate = Navigation.useNavigate();
  let url = Navigation.useUrl();
  let initialState$1 = React.useMemo(() => {
    let urlState = JsonCodec.decodeWith(QueryString.parse(url.query), codec);
    let match = url.query;
    if (urlState.TAG === "Ok" && match !== "") {
      return urlState._0;
    } else {
      return initialState;
    }
  }, []);
  let match = React.useReducer(reducer, initialState$1);
  let state = match[0];
  React.useEffect(() => {
    let query = QueryString.stringify(JsonCodec.encodeWith(state, codec));
    let route = url.pathname + "?" + query;
    if (query !== url.query) {
      navigate({
        replace: true
      }, route);
    }
  }, [state]);
  return [
    state,
    match[1]
  ];
}

let edgesPerPage = 10;

export {
  edgesPerPage,
  totalPages,
  connectionArguments,
  initialState,
  reducer,
  nextPage,
  use,
}
/* react Not a pure module */
